import { carouselItems, rankings, appInfo, topics } from '@/mocks/homeData';

// ---- Carousel Section ----

export function CarouselSection() {
  return (
    <section id="carousel" className="py-10 md:py-14 bg-background-100">
      <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6">
        <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-8">焦点专题</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {carouselItems.map((item) => (
            <article key={item.id} className="relative bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-colors cursor-pointer group">
              <div className="relative w-full aspect-[2/1] overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  title={item.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5 text-background-50">
                <span className="text-[10px] px-2 py-0.5 bg-accent-500 rounded-full font-medium">{item.subtitle}</span>
                <h3 className="text-lg font-medium mt-2 mb-1">{item.title}</h3>
                <p className="text-xs text-background-200 leading-relaxed line-clamp-2">{item.desc}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---- Ranking Section ----

export function RankingSection() {
  return (
    <section id="ranking" className="py-10 md:py-14 bg-background-50">
      <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6">
        <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-2">热度榜单</h2>
        <p className="text-sm text-foreground-500 text-center mb-8">全网影迷都在看的热门影片</p>
        <div className="max-w-2xl mx-auto flex flex-col gap-2">
          {rankings.map((item) => (
            <div key={item.id} className="flex items-center gap-4 bg-background-100 rounded-lg px-4 py-3 border border-background-200/70 hover:border-primary-200 transition-colors cursor-pointer">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                item.rank <= 3 ? 'bg-accent-500 text-background-50' : 'bg-background-200 text-foreground-500'
              }`}>
                {item.rank}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground-900 truncate">{item.title}</span>
                  {item.hot && <i className="ri-fire-line w-4 h-4 flex items-center justify-center text-accent-500 flex-shrink-0"></i>}
                </div>
              </div>
              <span className="text-[10px] px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded-full font-medium whitespace-nowrap flex-shrink-0">{item.tag}</span>
              <span className="text-xs text-foreground-500 whitespace-nowrap flex-shrink-0">{item.views}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---- App Section ----

export function AppSection() {
  return (
    <section id="app" className="py-10 md:py-14 bg-background-100">
      <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6">
        <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-8">移动观影</h2>
        <div className="max-w-2xl mx-auto bg-background-50 rounded-lg p-6 md:p-8 border border-background-200/70 flex flex-col md:flex-row items-center gap-6">
          <div className="w-32 h-32 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-smartphone-line text-accent-600 text-3xl w-10 h-10 flex items-center justify-center"></i>
          </div>
          <div className="text-center md:text-left flex-1">
            <h3 className="text-lg font-medium text-foreground-900 mb-2">{appInfo.title}</h3>
            <p className="text-sm text-foreground-600 leading-relaxed mb-4">{appInfo.description}</p>
            <div className="flex flex-wrap gap-2 justify-center md:justify-start">
              {appInfo.features.map((feat, idx) => (
                <span key={idx} className="text-[10px] px-2 py-1 bg-secondary-100 text-secondary-700 rounded-full font-medium">{feat}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---- Topics Section ----

export function TopicsSection() {
  return (
    <section id="topics" className="py-10 md:py-14 bg-background-50">
      <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6">
        <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-8">热门话题</h2>
        <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-3">
          {topics.map((topic) => (
            <a
              key={topic.id}
              href="#topics"
              className="inline-flex items-center gap-2 px-4 py-3 bg-background-100 rounded-lg border border-background-200/70 hover:border-primary-300 hover:bg-primary-50 transition-colors cursor-pointer group"
            >
              <i className={`${topic.icon} w-5 h-5 flex items-center justify-center text-secondary-500 group-hover:text-primary-500 transition-colors`}></i>
              <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">{topic.name}</span>
              <span className="text-[10px] text-foreground-400">{topic.count}</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}